/**
 * Discover Workspaces HTTP client (avatar menu > Discover Workspaces, and the
 * Joining Request card under Settings > User Management).
 *
 * Every call resolves the shared `{ ok, data | error, status }` shape from
 * `workspacesApi.sendJson`.  Backend routes live in
 * `backend/app/routes/workspaces_route.py`:
 *   GET    /workspaces/discover
 *   GET    /workspaces/discover/{id}/members
 *   POST   /workspaces/{id}/join-requests
 *   DELETE /workspaces/{id}/join-requests/mine
 *   GET    /workspaces/{id}/join-requests              (Admin)
 *   POST   /workspaces/{id}/join-requests/{rid}/accept (Admin)
 *   POST   /workspaces/{id}/join-requests/{rid}/decline (Admin)
 */
import { sendJson } from './workspacesApi';

const workspacePath = (workspaceId, suffix = '') => `workspaces/${encodeURIComponent(workspaceId)}${suffix}`;

const requestPath = (workspaceId, requestId, action) =>
    workspacePath(workspaceId, `/join-requests/${encodeURIComponent(requestId)}/${action}`);

/** `{ workspaces: [...], people: { email: { name, picture } } }` for the caller's organization. */
export const fetchDiscoverWorkspaces = () => sendJson('GET', 'workspaces/discover');

/** `{ workspace_id, workspace_name, members: [{ email, name, picture, role, is_owner }] }`. */
export const fetchDiscoverMembers = (workspaceId) =>
    sendJson('GET', `workspaces/discover/${encodeURIComponent(workspaceId)}/members`);

/** "Ask to join": 201 with the new pending request; 409 when already a member or already pending. */
export const requestToJoin = (workspaceId) => sendJson('POST', workspacePath(workspaceId, '/join-requests'));

/** "Cancel request": withdraws the caller's pending request for the workspace. */
export const cancelJoinRequest = (workspaceId) => sendJson('DELETE', workspacePath(workspaceId, '/join-requests/mine'));

/** Admin: `{ requests: [{ id, email, name, picture, requested_at }] }`, oldest first. */
export const fetchJoinRequests = (workspaceId) => sendJson('GET', workspacePath(workspaceId, '/join-requests'));

/** Admin: grant `role` (Admin | Editor | Viewer) and add the requester to the workspace. */
export const acceptJoinRequest = (workspaceId, requestId, role) =>
    sendJson('POST', requestPath(workspaceId, requestId, 'accept'), { role });

/** Admin: decline the request; the requester is emailed. */
export const declineJoinRequest = (workspaceId, requestId) =>
    sendJson('POST', requestPath(workspaceId, requestId, 'decline'));
